<template>
    <div :class="containerClass">
        <component
            :is="componentIcon"
            v-if="!isMobile"
            :class="componentClass"
            @click="state = State.Minimized"
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted } from 'vue';
    import { Position, Variant } from '@keyvue/one-api-client';
    import useWidgetState, { State } from '@/composables/useWidgetState';
    import useConfiguration from '@/composables/useConfiguration';
    import useWindowSize from '@/composables/useWindowSize';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';
    import useAnimating from '@/composables/useAnimating';

    const { animating, startAnimating } = useAnimating();
    const { state } = useWidgetState();
    const { assistent } = useConfiguration();
    const { isMobile } = useWindowSize();
    const {
        variantForIcon,
        positionForIcon,
        componentIcon,
    } = useWidgetAppearance();

    const componentClass = computed(() => {
        return {
            'desktop-minimize-button': true,
            'desktop-minimize-button--animating': animating.value,
            'desktop-minimize-button--square': !Variant.SQUARE.equals(variantForIcon.value),
            'desktop-minimize-button--rectangle': Variant.SQUARE.equals(variantForIcon.value),
        };
    });

    const containerClass = computed(() => {
        return {
            'desktop-minimize-button__container': true,
            'desktop-minimize-button__container--left': Position.LEFT.equals(positionForIcon.value),
            'desktop-minimize-button__container--right': Position.RIGHT.equals(positionForIcon.value),
            'desktop-minimize-button__container--top-left': Position.TOP_LEFT.equals(positionForIcon.value),
            'desktop-minimize-button__container--top-right': Position.TOP_RIGHT.equals(positionForIcon.value),
            'desktop-minimize-button__container--bottom-left': Position.BOTTOM_LEFT.equals(positionForIcon.value),
            'desktop-minimize-button__container--bottom-right': Position.BOTTOM_RIGHT.equals(positionForIcon.value),
        };
    });

    onMounted(() => {
        const widget = assistent.value?.widget;
        if (!widget || widget.hintAfterSeconds === 0) {
            return;
        }

        startAnimating(
            widget.hintAfterSeconds,
            widget.repeatAfterSeconds,
        );
    });
</script>

<style scoped lang="scss">
    .desktop-minimize-button {
        color: var(--color-balloon);
        filter: drop-shadow(4px 4px 6px rgba(0, 0, 0, 0.3));
        pointer-events: auto;
        transform-origin: bottom center;
        transition: transform 200ms ease;
        cursor: pointer;

        &--square {
            max-width: 80px;
        }

        &--rectangle {
            max-width: 124px;
            max-height: 124px;
        }

        &--animating {
            animation: wiggle 1s ease-in-out;
        }

        &:hover, &:active {
            transform: scale(.95);
        }

        &__container {
            position: fixed;
            inset: 1rem;
            pointer-events: none;
            user-select: none;
            display: flex;
            flex-direction: column;

            &--left {
                justify-content: center;
                align-items: start;
            }

            &--right {
                justify-content: center;
                align-items: end;
            }

            &--top-left {
                justify-content: start;
                align-items: start;
                flex-direction: column-reverse;
            }

            &--top-right {
                justify-content: start;
                align-items: end;
                flex-direction: column-reverse;
            }

            &--bottom-left {
                justify-content: end;
                align-items: start;
            }

            &--bottom-right {
                justify-content: end;
                align-items: end;
            }

            &--bottom-left > .desktop-minimize-button--rectangle,
            &--bottom-right > .desktop-minimize-button--rectangle {
                max-height: 48px;
                max-width: 124px;
            }
        }
    }
</style>
